import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import WhatsAppFloating from "@/components/feature/WhatsAppFloating";
import CookieBanner from "@/components/feature/CookieBanner";
import SEO from "@/components/feature/SEO";
import SocialProofSection from "@/components/feature/SocialProofSection";
import ZoneNeighborhoods from "./ZoneNeighborhoods";
import ZonePricing from "./ZonePricing";
import ZoneFAQ from "./ZoneFAQ";
import ZoneQuoteForm from "./ZoneQuoteForm";
import { HeroFull } from '@/components/HeroFull';
import { HERO_IMAGE_DEFAULT } from '@/config/images';
import { getConditionsForCity } from "@/config/city-conditions";
import { CITY_TYPE_MAP } from "@/config/city-faqs";

export interface CityZoneHubProps {
  name: string;
  slug: string;
  poblacion: number;
  lat: number;
  lng: number;
  serviceSlug?: string;
  neighborhoodsText: string;
  faqItems: { question: string; answer: string }[];
}

export default function CityZoneHub({
  name,
  slug,
  poblacion,
  lat,
  lng,
  serviceSlug,
  neighborhoodsText,
  faqItems,
}: CityZoneHubProps) {
  const sSlug = serviceSlug ?? slug;
  const waMsg = "Hola, me interesa info sobre servicios en " + name;
  const waUrl = "https://wa.me/34627213347?text=" + encodeURIComponent(waMsg);

  const cityType = CITY_TYPE_MAP[slug] || 'interior';
  const conditions = getConditionsForCity(slug, cityType);

  const tierMessages = {
    'capital': {
      visit: `Visita en menos de 4h en ${name} centro`,
      direct: `Técnico propio en San Andrés`,
      quote: `Diagnóstico inmediato in situ`,
      warranty: `2 años en piezas y mano de obra`,
    },
    'costera': {
      visit: `Visita en 24h en ${name} y costa`,
      direct: `Sin call centers ni plataformas`,
      quote: `Presupuesto gratuito en 24h`,
      warranty: `2 años incluso en zona salitre`,
    },
    'interior': {
      visit: `Visita en 24-48h hasta ${name}`,
      direct: `Trato directo con el técnico`,
      quote: `Presupuesto sin compromiso`,
      warranty: `2 años con garantía total`,
    },
  };

  const msg = tierMessages[cityType as keyof typeof tierMessages];

  const trustCards = [
    { icon: "ri-flashlight-line", emoji: "⚡", title: "Rapidez", desc: msg.visit },
    { icon: "ri-hand-heart-line", emoji: "🤝", title: "Trato directo", desc: msg.direct },
    { icon: "ri-money-euro-circle-line", emoji: "💰", title: "Presupuesto gratis", desc: msg.quote },
    { icon: "ri-shield-check-line", emoji: "🛡️", title: "Garantía 2 años", desc: msg.warranty },
  ];

  const serviceDescriptions = {
    'capital': {
      persianas: `Reparación urgente de persianas en pisos y edificios de ${name}. Motores Somfy, Cherubini, cintas y lamas.`,
      toldos: `Instalación de toldos motorizados en balcones y terrazas de ${name}. Cofre, brazo invisible, pérgolas.`,
      mosquiteras: `Mosquiteras enrollables y correderas para ventanas estándar de ${name} centro.`,
    },
    'costera': {
      persianas: `Reparación de persianas resistentes al salitre marino en ${name}. Motores IP44 anti-corrosión.`,
      toldos: `Toldos motorizados con tejido resistente UV y viento de poniente en ${name}.`,
      mosquiteras: `Mosquiteras imprescindibles en ${name}: enrollables y plisadas para terrazas.`,
    },
    'interior': {
      persianas: `Persianas con aislamiento térmico reforzado para el calor de ${name}. Aluminio, PVC, motorizadas.`,
      toldos: `Toldos para terrazas y patios en ${name}. Resistencia al sol intenso y vientos racheados.`,
      mosquiteras: `Mosquiteras a medida para casas rurales y viviendas tradicionales de ${name}.`,
    },
  };

  const descs = serviceDescriptions[cityType as keyof typeof serviceDescriptions];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Persianas Toldos Mosquiteras en ${name} | Servicio Completo 24h`}
        description={`Reparación e instalación en ${name}: persianas, toldos, mosquiteras. Equipo propio, visita 24h, presupuesto gratis.`}
        canonical={`/zonas/${slug}/`}
        ogImage={`https://persianas-toldos-malaga.es/og/og-zonas-${slug}.jpg`}
        ogType="website"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Zonas", path: "/zonas" },
          { name },
        ]}
      />
      <Header />
      <main>
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt={`Técnico de Persianas Toldos Málaga atendiendo en ${name}`}
          badge={`⚡ 24h · Diagnóstico GRATIS · ${name}`}
          title={`Persianas, Toldos y Mosquiteras en ${name}`}
          subtitle={`Cubrimos todo ${name} con técnico propio, sin intermediarios. Presupuesto gratuito en 24h.`}
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: `https://wa.me/34627213347?text=${encodeURIComponent(`Hola, me interesa info en ${name}`)}`, label: "💬 WhatsApp 627 21 33 47" }}
          trustSignals={["+10 años en Málaga", "Seguro RC", "Garantía 2 años", "Sin intermediarios"]}
        />

        {/* PARTICULARIDADES */}
        <section className="my-12 max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">
            {conditions.title}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg">
            {conditions.content}
          </p>
        </section>

        {/* PRUEBA SOCIAL */}
        <SocialProofSection />

        {/* ¿POR QUÉ ELEGIRNOS? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
                ¿Por qué elegirnos en {name}?
              </h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {trustCards.map((item, i) => (
                <motion.div
                  key={item.title}
                  className="bg-white rounded-2xl p-6 text-center border border-border/50"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                >
                  <span className="text-2xl mb-2 block">{item.emoji}</span>
                  <div className="w-10 h-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-body font-bold text-sm text-text-primary mb-1">
                    {item.title}
                  </h3>
                  <p className="font-body text-xs text-text-secondary">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* NUESTROS 3 SERVICIOS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-10">
              Nuestros 3 servicios en {name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <motion.div
                className="bg-white rounded-[20px] p-8 border-t-[6px] border-primary shadow-sm hover:shadow-md transition-shadow flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: 0 }}
              >
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                  <i className="ri-window-line text-2xl"></i>
                </div>
                <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                  Reparación de persianas en {name}
                </h3>
                <p className="font-body text-sm text-text-secondary leading-relaxed mb-5 flex-grow">
                  {descs.persianas}
                </p>
                <Link
                  to={`/persianas/${sSlug}/`}
                  className="inline-flex items-center gap-1.5 font-body font-semibold text-sm text-primary hover:text-primary-dark transition-colors"
                >
                  Más sobre persianas en {name} <i className="ri-arrow-right-line"></i>
                </Link>
              </motion.div>
              <motion.div
                className="bg-white rounded-[20px] p-8 border-t-[6px] border-secondary shadow-sm hover:shadow-md transition-shadow flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="w-12 h-12 bg-secondary/10 text-secondary rounded-xl flex items-center justify-center mb-5">
                  <i className="ri-sun-line text-2xl"></i>
                </div>
                <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                  Toldos en {name}
                </h3>
                <p className="font-body text-sm text-text-secondary leading-relaxed mb-5 flex-grow">
                  {descs.toldos}
                </p>
                <Link
                  to={`/toldos/${sSlug}/`}
                  className="inline-flex items-center gap-1.5 font-body font-semibold text-sm text-secondary hover:text-[#0284C7] transition-colors"
                >
                  Más sobre toldos en {name} <i className="ri-arrow-right-line"></i>
                </Link>
              </motion.div>
              <motion.div
                className="bg-white rounded-[20px] p-8 border-t-[6px] border-accent shadow-sm hover:shadow-md transition-shadow flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="w-12 h-12 bg-accent/10 text-accent rounded-xl flex items-center justify-center mb-5">
                  <i className="ri-bug-line text-2xl"></i>
                </div>
                <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                  Mosquiteras en {name}
                </h3>
                <p className="font-body text-sm text-text-secondary leading-relaxed mb-5 flex-grow">
                  {descs.mosquiteras}
                </p>
                <Link
                  to={`/mosquiteras/${sSlug}/`}
                  className="inline-flex items-center gap-1.5 font-body font-semibold text-sm text-accent hover:text-[#EAB308] transition-colors"
                >
                  Más sobre mosquiteras en {name} <i className="ri-arrow-right-line"></i>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CONOCEMOS {CIUDAD} */}
        <ZoneNeighborhoods cityName={name} text={neighborhoodsText} />

        {/* PRECIOS ORIENTATIVOS */}
        <ZonePricing cityName={name} slug={slug} />

        {/* FAQ LOCAL */}
        <ZoneFAQ cityName={name} items={faqItems} />

        {/* EXPLORA SERVICIOS ESPECÍFICOS */}
        <section className="my-12 max-w-3xl mx-auto px-4 border-t pt-8">
          <h3 className="text-lg font-semibold mb-3">Explora servicios específicos en {name}</h3>
          <ul className="space-y-2">
            <li>
              <Link to={`/persianas/${slug}`} className="text-blue-600 hover:underline">
                → Reparación de persianas en {name} (motor, cinta, lamas)
              </Link>
            </li>
            <li>
              <Link to={`/toldos/${slug}`} className="text-blue-600 hover:underline">
                → Instalación y reparación de toldos en {name}
              </Link>
            </li>
            <li>
              <Link to={`/mosquiteras/${slug}`} className="text-blue-600 hover:underline">
                → Mosquiteras a medida en {name}
              </Link>
            </li>
          </ul>
        </section>

        {/* FORMULARIO PRESUPUESTO */}
        <ZoneQuoteForm cityName={name} slug={slug} />
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}